import { eventCenter } from '../event-center/index'
import { cloneDeep, has, forEach as ForEach } from 'lodash-es'
import type { ComponentOperationData } from '../types/index'
import { useProject, useEvent, useWidget } from '@haohan/clw-store'
import {
  getEventValueByValueType,
  handleBehaviorByApi,
  handleBehaviorByDialog,
  handleBehaviorByLogicFlow,
  handleBehaviorByRunIn,
  handleBehaviorByToOutUrl,
  handleHighLevelFun,
  handleSendEventValue,
  buildRegisterComponentInfo,
  getEventKey
} from '../utils/index'
import { getFlatWidgetList, getCompChildren } from '@haohan/clw-utils'
import { computed } from 'vue'
export const useEventCenter = () => {
  const projectStore = useProject()
  const eventStore = useEvent()
  const widgetStore = useWidget()
  /**
   * 初始化必要信息
   * @param info 初始信息
   * @param clear 是否清除所有交互信息
   */
  const initInfo = (
    pageInfo: { sendFilters: WidgetEvent[]; receiveFilters: WidgetEvent[] },
    clear = true
  ) => {
    console.log('useEventCenter ======>', '初始化交互信息')
    if (clear) {
      eventCenter.clear()
    }
    // 初始化数据池
    eventCenter.initMutualPool(pageInfo.sendFilters)
    console.log(
      'useEventCenter ======>',
      '交互中心准备完毕',
      cloneDeep(eventCenter._eventValuePool)
    )
    // 保存当前页所有接收事件
    // eventCenter.setReceiveEvents(pageInfo.receiveFilters)
  }
  /**
   * 注册组件
   * @param widget 当前组件
   * @param onOperation 操作方法
   * @param getEventValueCanUseObj 获得值方法
   * @returns
   */
  const registerComponent = (
    widget: Widget,
    onOperation: (operation: string, data: ComponentOperationData) => void,
    getEventValueCanUseObj: () => { [key: string]: any }
  ) => {
    // 检查 widget.key 是否存在
    if (!widget.key) {
      console.warn(`Widget key is missing, cannot register component.`)
      return
    }
    // console.log('registerComponent', widget.title)
    const info = buildRegisterComponentInfo(widget, onOperation, getEventValueCanUseObj)
    // 注册组件
    eventCenter.register(info)
    // 执行组件中立即执行的交互
    doRunPromptlyEvent(widget, getEventValueCanUseObj ? getEventValueCanUseObj() : {})
  }
  /**
   * 主动清空指定接收事件的信息
   * @param receiveEvents 接收事件
   */
  const clearEventInfo = (receiveEvents: WidgetEvent[]) => {
    eventCenter.clearEventInfo(receiveEvents)
  }

  const setReceiveEventBindValue = (
    data: ComponentOperationData,
    setOperation: (bindValue: string, data: any) => void
  ) => {
    data?.receiveEvents.forEach((receiveEvent: WidgetEvent) => {
      if (receiveEvent.reviceBindValue && receiveEvent.reviceBindValue !== '') {
        setOperation(
          receiveEvent.reviceBindValue,
          data.data[receiveEvent.receiveEventKey as string].value
        )
      }
    })
  }
  /**
   * 执行组件中需要立即触发的事件
   * @param item 当前组件
   * @param canSelectBindValues 可获取的值
   */
  const doRunPromptlyEvent = (item: Widget, canSelectBindValues: {}) => {
    // 修复如标签页这种静态数据，立即执行没有及时更新交互池中的数据的问题
    // 条件筛选组件需要等待它所有的内容准备完毕后才能触发，因此交由它主动触发
    // 条件1：静态数据无法等待直接触发
    // 条件2、3、4：无数据绑定功能、或者未绑定数据源，直接触发
    // 条件5：没有开启默认选中，即使是绑定的数据源也直接触发
    if (
      !['HHCustomQuery'].includes(item.type) &&
      !(has(item, 'prop') && has(item.prop, 'hasDefault') && item.prop.hasDefault === '1') &&
      (item.dataSourceType === 'LocalData' ||
        !item.dataSourceSetting ||
        !has(item, 'dataSourceType') ||
        !has(item, 'dataProp') ||
        (has(item, 'dataProp') && has(item.dataProp, 'tableId') && item.dataProp.tableId === '') ||
        (has(item, 'prop') && has(item.prop, 'hasDefault') && item.prop.hasDefault === '0'))
    ) {
      // 获得该组件中所有的运行时事件
      const doSendEvents = item.events!.filter(
        (event: WidgetEvent) => event.eventType === 'send' && event.action === 'promptlyFilters'
      )
      sendEventByEvent(item, doSendEvents, canSelectBindValues)
    }
  }

  const sendEventByTriggerType = (item: Widget, triggerType: string, canSelectBindValues: {}) => {
    item
      .events!.filter((e: WidgetEvent) => e.eventType === 'send' && e.triggerType === triggerType)
      .forEach((e: WidgetEvent) => {
        const val = handleSendEventValue(item, e, canSelectBindValues)
        // 值处理
        sendEvent(e, val)
      })
  }
  /**
   * 发送指定发送事件
   * @param item 组件
   * @param doSendEvent 发送事件
   * @param canSelectBindValues 可供选中的值
   */
  const sendEventByEvent = (item: Widget, doSendEvent: WidgetEvent[], canSelectBindValues: {}) => {
    doSendEvent
      .filter((e: WidgetEvent) => e.eventType === 'send')
      .forEach((e: WidgetEvent) => {
        const val = handleSendEventValue(item, e, canSelectBindValues)
        // 值处理
        sendEvent(e, val)
      })
  }

  // TODO：事件值处理
  /**
   * 执行发送事件，具体这里的事件值交由不同的组件来处理
   * @param event 发送事件
   * @param val 事件值
   */
  const sendEvent = (event: WidgetEvent, val: any) => {
    // 根据绑定值类型，处理值
    const eventValue = getEventValueByValueType(event.bindValueType!, val)
    // 更新全局变量中的值
    if (event.eventType === 'send' && event.comParam && event.comParam !== '') {
      if (eventStore.commonParameters[event.comParam]) {
        eventStore.updateCommonParameters(event.comParam, val)
      }
    }
    // 如果由绑定高级处理，则在这步进行处理
    if (event.highLevelFun && event.highLevelFun !== '') {
      handleHighLevelFun(event.highLevelFun, val)
    }
    // 更新交互池中的对应值
    eventCenter.updateMutualPool(event.key, eventValue, event.currentPageId!)
    console.log('sendEvent', event.key, event.currentPageId!, eventValue)
    // 根据不同交互类型，进行处理
    if (event.behavior && event.behavior === 'runIn') {
      handleBehaviorByRunIn(eventStore, projectStore, event, eventValue)
    } else if (event.behavior && event.behavior === 'dialog') {
      handleBehaviorByDialog(eventStore, projectStore, event, eventValue)
    } else if (event.behavior && event.behavior === 'api') {
      handleBehaviorByApi(event, eventValue)
    } else if (event.behavior && event.behavior === 'logicFlow') {
      handleBehaviorByLogicFlow(event, eventValue)
    } else if (event.behavior && event.behavior === 'toOutUrl') {
      handleBehaviorByToOutUrl(event)
    } else {
      eventCenter.emitEvent(event, eventValue)
    }
  }
  /**
   * 返回指定操作方法的事件值
   * @param receiveEvents 接收事件
   * @param operation 执行需要过滤的接收事件，传空字符串则不过滤
   * @returns 返回指定操作方法的事件值
   */
  const getEventValuesByOperation = (receiveEvents: WidgetEvent[], operation = 'reloadByEvent') => {
    return eventCenter.getEventValuesByReceiveEvent(
      receiveEvents.filter((re: WidgetEvent) => {
        let flag = operation === '' ? true : re.performOperation === operation
        // 如果是reloadByEvent，则要包含onlyReceiveEvent事件，以便实现点击按钮才请求数据
        if (!flag && operation === 'reloadByEvent') {
          flag = re.performOperation === 'onlyReceiveEvent'
        }
        return re.eventType === 'receive' && flag
      })
    )
  }

  const clearEventListener = () => {
    eventCenter.clear()
    console.log('useEventCenter ======>', '清空交互信息')
  }
  /**
   * 返回最新更新的数据
   * @param data 数据池
   * @returns 返回最新更新的数据
   */
  const getLasterUpdateValue = (data: MutualPool) => {
    const keys = Object.keys(data)
    if (keys.length === 1) {
      return data[keys[0]]
    }
    let mpd = data[keys[0]]
    keys.forEach((key: string) => {
      // 只取最新的一个数据
      if (mpd.updateTime < data[key].updateTime) {
        mpd = data[key]
      }
    })
    return mpd
  }
  /**
   * 重新构建事件
   * @param widgets 扁平化后的组件集合
   */
  const rebuildEventCenterInfo = (widgets: Widget[]) => {
    console.log('rebuildEventCenterInfo ==>', widgets)
    // 清空事件监听以及缓存池内容
    eventCenter.clearEmitter()
    // eventCenter.clearEventValuePool()
    widgets.forEach((wd: Widget) => {
      // 重建事件监听
      eventCenter.registerByComponent(
        buildRegisterComponentInfo(
          wd,
          () => {},
          () => {}
        )
      )
    })
  }
  /**
   * 删除发送事件的旧有数据
   * @param sendEvent 发送事件
   */
  const clearSendEventInfo = (sendEvent: WidgetEvent[]) => {
    sendEvent.forEach((event: WidgetEvent) => {
      eventCenter.deleteMutualPoolByKey(event.key)
    })
  }
  /**
   * 重建发送事件交互池中的值
   * @param sendEvent 发送事件
   */
  const rebuildSendEventInfo = (sendEvent: WidgetEvent[]) => {
    eventCenter.rebuildSendEventInfo(sendEvent)
  }
  /**
   * 根据删除的组件，重建事件信息
   * @param widget 删除的组件
   */
  const rebuildEventInfoByRemoveWidget = (widget: Widget) => {
    if (widget.events && widget.events?.length > 0) {
      // 获得当前页面组件编排信息
      const flatWidgetList = getFlatWidgetList(widgetStore.widgetDataList)
      // 遍历删除组件中的
      const deleteSendEvent = widget.events?.filter((e: WidgetEvent) => e.eventType === 'send')
      // const deleteReceiveEvent = widget.events?.filter(
      //   (e: WidgetEvent) => e.eventType === 'receive'
      // )
      // 清空事件监听，避免组件刷新时，事件中心未准备好导致的异常
      eventCenter.clearEmitter()
      // 删除发送事件信息
      clearSendEventInfo(deleteSendEvent || [])
      const deleteSendEventKeys = deleteSendEvent?.map((event: WidgetEvent) => event.key)
      if (deleteSendEventKeys?.length > 0) {
        flatWidgetList.flatWidgetList.forEach((wd: Widget) => {
          if (wd.events) {
            // 删除监听删除组件发送事件的接收事件
            wd.events = wd.events?.filter(
              (e: WidgetEvent) =>
                e.eventType === 'send' ||
                (e.eventType === 'receive' && !deleteSendEventKeys.includes(e.receiveEventKey!))
            )
          }
        })
      }
      // 重建
      rebuildEventCenterInfo(flatWidgetList.flatWidgetList)
    }
    const children = getCompChildren(widget)
    if (children.length) {
      for (const childObj of children) {
        childObj.list.forEach((wd: Widget) => rebuildEventInfoByRemoveWidget(wd))
      }
    }
  }

  const updateComponentRegisterInfo = (widget: Widget) => {}
  /**
   * 更新组件的接收事件
   * @param widget 组件
   */
  const updateRegisterComponentInfo = (widget: Widget) => {
    // const _receiveEvents = widget.events?.filter((e: WidgetEvent) => e.eventType === 'receive')
    const info = buildRegisterComponentInfo(
      widget,
      () => {},
      () => {}
    )
    eventCenter.updateRegisterComponent(info, ['groupOp', 'receiveEvents'])
  }
  /**
   * 判断组件交互是否准备完毕
   * @param widget 当前组件
   * @returns 是否准备完毕
   */
  const isReady = (widget: Widget) => {
    const readyStatus = computed(() => {
      if (!has(widget, 'events') || widget.events.length === 0) {
        return true
      }
      const receiveEvents = widget.events?.filter((e) => e.eventType === 'receive') || []
      let readly = true
      // 避免当切换页面时，有残留的组件进行了判断，导致请求错误问题
      if (
        readly &&
        has(widget, 'dataProp') &&
        has(widget.dataProp, 'tableId') &&
        ![undefined, ''].includes(widget.dataProp!.tableId)
      ) {
        if (!eventStore.pageInfo.widgetDataIdKeys.includes(widget.dataProp!.tableId)) {
          readly = false
          return false
        }
      }
      ForEach(receiveEvents, (re: any) => {
        const _rKey = getEventKey(re.receiveEventKey!, widget.currentPageId)
        if (
          eventCenter._eventValuePool[_rKey] &&
          eventCenter._eventValuePool[_rKey].status === 'waiting'
        ) {
          readly = false
          return false
        }
      })
      // console.log('isReady', widget.title, cloneDeep(eventCenter._eventValuePool))
      return readly
    })
    return readyStatus
  }

  /**
   * 判断组件交互是否准备完毕
   * @param widget 当前组件
   * @returns 是否准备完毕
   */
  const readyStatus = (widget: Widget) => {
    const readyStatus = computed(() => {
      if (!has(widget, 'events') || widget.events.length === 0) {
        return true
      }
      const receiveEvents = widget.events?.filter((e) => e.eventType === 'receive') || []
      let readly = true
      // 避免当切换页面时，有残留的组件进行了判断，导致请求错误问题
      if (
        readly &&
        has(widget, 'dataProp') &&
        has(widget.dataProp, 'tableId') &&
        ![undefined, ''].includes(widget.dataProp!.tableId)
      ) {
        if (!eventStore.pageInfo.widgetDataIdKeys.includes(widget.dataProp!.tableId)) {
          readly = false
          return false
        }
      }
      ForEach(receiveEvents, (re: any) => {
        const _rKey = getEventKey(re.receiveEventKey!, widget.currentPageId)
        if (
          eventCenter._eventValuePool[_rKey] &&
          eventCenter._eventValuePool[_rKey].status === 'waiting'
        ) {
          readly = false
          return false
        }
      })
      console.log('isReady', readly, widget, cloneDeep(eventCenter._eventValuePool))
      return readly
    })
    return readyStatus
  }

  const readyStatusByReceiveEvent = (receiveEvents: WidgetEvent[]) => {
    const readyStatus = computed(() => {
      const _keys = receiveEvents.map((e) => e.receiveEventKey) || []
      let readly = true
      ForEach(_keys, (k: any) => {
        if (eventCenter._eventValuePool[k] && eventCenter._eventValuePool[k].status === 'waiting') {
          readly = false
          return false
        }
      })
      // console.log('isReady', receiveEvents, cloneDeep(eventCenter._eventValuePool))
      return readly
    })
    return readyStatus
  }

  return {
    eventCenter,
    initInfo,
    sendEvent,
    sendEventByEvent,
    registerComponent,
    getEventValuesByOperation,
    sendEventByTriggerType,
    clearEventListener,
    setReceiveEventBindValue,
    getLasterUpdateValue,
    clearEventInfo,
    rebuildEventCenterInfo,
    clearSendEventInfo,
    rebuildSendEventInfo,
    rebuildEventInfoByRemoveWidget,
    updateComponentRegisterInfo,
    updateRegisterComponentInfo,
    isReady,
    readyStatus,
    readyStatusByReceiveEvent
  }
}
